import 'theme';

const HINT_KEY = 'floyd:navigation:arrival';
const ARRIVAL_CLASS = 'floyd-navigation-arrival';
const DESKTOP = '(min-width: 1000px) and (hover: hover) and (pointer: fine)';
const CLOSE_DELAY = 350;
const OPEN_DELAY = 250;
const GAP_CLOSE_DELAY = 800;
const controllers = new WeakMap();

// A named subclass, not a patch to Prestige's registry or prototype. Native
// summary links, click/touch, keyboard handling and details semantics are kept.
class FloydMegaMenu extends customElements.get('mega-menu-disclosure') {
  animation = null;
  openingFromClosed = false;

  connectedCallback() {
    super.connectedCallback();
    const header = this.closest('.floyd-navigation');
    if (header && !controllers.has(header)) controllers.set(header, new Navigation(header));
    if (header && this.disclosureElement.hasAttribute('data-floyd-navigation-restored')) {
      this.disclosureElement.removeAttribute('data-floyd-navigation-restored');
      controllers.get(header).active = this;
      // Native open installs keyboard/outside listeners. Since details is already
      // open, Prestige does not replay the entrance animation.
      this.open({ instant: true });
    }
  }

  disconnectedCallback() {
    this.close();
    this.animation?.cancel();
    super.disconnectedCallback();
  }

  open(options) {
    this.openingFromClosed = !this.disclosureElement.open;
    return super.open(options);
  }

  createShowAnimationControls() { return this.transitionTo(1); }
  createHideAnimationControls() { return this.transitionTo(0); }

  transitionTo(opacity) {
    const from = opacity && this.openingFromClosed ? 0 : getComputedStyle(this.contentElement).opacity;
    this.animation?.cancel();
    const animation = this.contentElement.animate([{ opacity: from }, { opacity }], {
      duration: matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 220,
      fill: 'both',
      easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
    });
    this.animation = animation;
    return {
      // Prestige removes [open] only for a completed hide. Cancellation must
      // resolve undefined so a reopened menu is not closed by an old promise.
      finished: animation.finished.then(() => true, () => undefined),
      finish: () => animation.finish(),
    };
  }
}

class Navigation {
  constructor(header) {
    this.header = header;
    this.abort = new AbortController();
    this.arriving = document.documentElement.classList.contains(ARRIVAL_CLASS);
    this.active = null;
    this.closeTimer = null;
    this.openTimer = null;
    this.pendingMenu = null;
    const capture = { capture: true, signal: this.abort.signal };
    const options = { signal: this.abort.signal };

    header.addEventListener('mouseover', event => this.over(event), capture);
    header.addEventListener('mouseout', event => this.out(event), capture);
    header.addEventListener('click', event => this.rememberNavigation(event), capture);
    this.arrivalPointerMove = event => {
      if (!this.arriving || (!event.movementX && !event.movementY)) return;
      document.removeEventListener('pointermove', this.arrivalPointerMove);
      this.arriving = false;
      if (header.contains(event.target)) this.over(event);
      else this.scheduleClose();
    };
    if (this.arriving) {
      document.addEventListener('pointermove', this.arrivalPointerMove, { ...options, passive: true });
    }
    document.addEventListener('keydown', event => {
      if (event.key === 'Escape' || event.key === 'Tab') {
        this.cancelOpen();
        this.releaseArrival();
      }
    }, options);
    document.addEventListener('click', event => {
      if (!header.contains(event.target)) this.releaseArrival();
    }, options);
    window.addEventListener('pageshow', event => {
      if (event.persisted) this.releaseArrival();
    }, options);
    document.addEventListener('shopify:section:unload', event => {
      if (event.target.contains(header)) this.destroy();
    }, options);
  }

  isHover(menu) { return matchMedia(DESKTOP).matches && menu?.getAttribute('trigger') === 'hover'; }

  over(event) {
    const menu = event.target.closest('floyd-mega-menu');
    // Replace only the native bubbling hover handlers for these desktop menus.
    // Click, keyboard, focus and all mobile events continue through Prestige.
    if (this.isHover(menu) && event.type === 'mouseover') event.stopPropagation();
    if (!matchMedia(DESKTOP).matches) return;
    if (!menu && (this.active || this.arriving)) {
      this.scheduleClose(GAP_CLOSE_DELAY);
      return;
    }
    clearTimeout(this.closeTimer);
    if (this.arriving || !this.isHover(menu)) return;
    if (this.active === menu && menu.disclosureElement.getAttribute('aria-expanded') === 'true') return;
    if (this.pendingMenu === menu) return;
    this.cancelOpen();
    this.pendingMenu = menu;
    this.openTimer = setTimeout(() => {
      this.pendingMenu = null;
      this.activate(menu);
    }, OPEN_DELAY);
  }

  activate(menu) {
    const switching = this.active && this.active !== menu;
    if (switching) {
      this.active.close();
      this.active.animation?.finish();
    }
    this.active = menu;
    menu.open({ instant: Boolean(switching) });
  }

  out(event) {
    const menu = event.target.closest('floyd-mega-menu');
    if (this.isHover(menu)) event.stopPropagation();
    if (this.pendingMenu && !this.pendingMenu.contains(event.relatedTarget)) this.cancelOpen();
    if (matchMedia(DESKTOP).matches && !this.header.contains(event.relatedTarget)) this.scheduleClose();
  }

  scheduleClose(delay = CLOSE_DELAY) {
    this.cancelOpen();
    clearTimeout(this.closeTimer);
    this.closeTimer = setTimeout(() => {
      this.active?.close();
      this.active = null;
      this.releaseArrival();
    }, delay);
  }

  releaseArrival() {
    document.removeEventListener('pointermove', this.arrivalPointerMove);
    this.arriving = false;
    document.documentElement.classList.remove(ARRIVAL_CLASS);
  }

  cancelOpen() {
    clearTimeout(this.openTimer);
    this.openTimer = null;
    this.pendingMenu = null;
  }

  rememberNavigation(event) {
    if (!matchMedia(DESKTOP).matches || event.button !== 0 ||
        event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target.closest('a, summary[data-follow-link]');
    if (!link?.closest('.header__primary-nav') || link.hasAttribute('download') ||
        (link.target && link.target !== '_self')) return;
    // A closed summary opens its menu instead of navigating.
    if (link.tagName === 'SUMMARY' && !link.parentElement.open) return;
    const href = link.getAttribute('href') || link.getAttribute('data-follow-link');
    if (!href) return;
    const target = new URL(href, location.href);
    if (target.origin !== location.origin ||
        (target.pathname === location.pathname && target.search === location.search)) return;
    try {
      const menu = link.closest('floyd-mega-menu');
      const menuKey = this.isHover(menu) && menu.disclosureElement.open &&
        menu.disclosureElement.getAttribute('aria-expanded') === 'true'
        ? menu.summaryElement.getAttribute('data-follow-link') : null;
      sessionStorage.setItem(HINT_KEY, JSON.stringify({ url: target.href, time: Date.now(), menu: menuKey }));
    } catch (_) { /* Navigation must never depend on storage availability. */ }
  }

  destroy() {
    this.cancelOpen();
    clearTimeout(this.closeTimer);
    this.abort.abort();
    this.releaseArrival();
    controllers.delete(this.header);
  }
}

if (!customElements.get('floyd-mega-menu')) customElements.define('floyd-mega-menu', FloydMegaMenu);

function initializeHeaders(root) {
  root.querySelectorAll('.floyd-navigation').forEach(header => {
    if (!controllers.has(header)) controllers.set(header, new Navigation(header));
  });
}
initializeHeaders(document);
document.addEventListener('shopify:section:load', event => initializeHeaders(event.target));

